import { MouseEvent } from 'react'
import { Box, Chip, IconButton, Link as MuiLink, Switch, Tooltip, Typography } from '@mui/material'
import ArticleIcon from '@mui/icons-material/Article'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import NoPhotographyIcon from '@mui/icons-material/NoPhotography'
import PaymentIcon from '@mui/icons-material/Payment'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import SensorsIcon from '@mui/icons-material/Sensors'
import LockIcon from '@mui/icons-material/Lock'
import VideocamIcon from '@mui/icons-material/Videocam'
import TimelineIcon from '@mui/icons-material/Timeline'
import {
  GridColDef,
  GridEditInputCell,
  GridEditSingleSelectCell,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridValueOptionsParams,
  useGridApiContext,
} from '@mui/x-data-grid-pro'
import { getGridSingleSelectOperators } from '@mui/x-data-grid'
import { DEFAULT_V3_COLUMN_ORDER, INVENTORY_V3_SINGLE_SELECT_HEADER_FILTER_CLASS } from './inventoryV3DefaultTemplate'
import { inventoryV3DateCellValue, inventoryV3DateStoredValue } from './inventoryV3DateFilter'
import { INVENTORY_V3_DATE_FILTER_OPERATORS } from './inventoryV3DateFilterOperators'
import { SingleSelectHeaderMultiFilter } from './SingleSelectHeaderMultiFilter'
import { VEHICLE_TIERS, Vehicle } from '@otw/models/inventory'
import {
  boxTruck as boxTruckIcon,
  cargoVan as cargoVanIcon,
  cutaway as cutawayIcon,
  stepVan as stepVanIcon,
} from 'assets'
import {
  conditionOptions,
  dealQualityOptions,
  getVehicleModelOptions,
  getVehicleSizeOptions,
  getVehicleYearOptions,
  makeOptions,
  vehicleFuelOptions,
  vehicleStatusOptions,
  vehicleTypeOptions,
} from 'constants/inventory'
import { getVehicleStatusStyles } from 'core/utils/inventoryUtils'
import { getDealQualityInfo } from 'hooks/graybook/useCalculateDeal'
import { formatCurrency } from 'utils/formatCurrency'
import { InventoryV3Row } from './buildInventoryV3Rows'
import states from 'states-us'
import {
  INVENTORY_V3_DATE_FIELDS,
  INVENTORY_V3_NUMBER_FIELDS,
  parseInventoryV3Date,
  isIncompleteInventoryV3NumberInput,
  parseInventoryV3Number,
} from './inventoryV3CellValidation'
import { verifyProperEmail } from 'core/utils/verifyProperEmail'

export interface InventoryV3ColumnParams {
  isAdmin: boolean
  isBabyAdmin: boolean
  canModify: boolean
  canViewPurchasePrice: boolean
  canViewProfit: boolean
  canViewProfitRealized: boolean
  canMarkSold: boolean
  canMarkSoldElsewhere: boolean
  canMarkReadyForSale: boolean
  onEditVehicleClick: (vehicle: Vehicle) => void
  onManagePhotosClick: (vehicle: Vehicle) => void
  onManageDocumentsClick: (vehicle: Vehicle, tab?: number) => void
  onOpenTimelineClick: (vehicle: Vehicle) => void
  onBooleanFieldChange: (vehicleId: string, field: string, value: boolean) => void
}

const getStatusValueOptions = ({
  canModify,
  canMarkSold,
  canMarkSoldElsewhere,
}: {
  canModify: boolean
  canMarkSold: boolean
  canMarkSoldElsewhere: boolean
}): string[] => {
  if (canModify) {
    if (canMarkSold && canMarkSoldElsewhere) return vehicleStatusOptions
    if (canMarkSold && !canMarkSoldElsewhere) {
      return vehicleStatusOptions.filter(status => status !== 'Sold elsewhere')
    }
    if (!canMarkSold && canMarkSoldElsewhere) {
      return vehicleStatusOptions.filter(status => status !== 'Sold')
    }
    return vehicleStatusOptions.filter(status => !status.toLowerCase().includes('sold'))
  }
  if (canMarkSold && canMarkSoldElsewhere) {
    return vehicleStatusOptions.filter(
      status => status === 'Available' || status === 'Sold' || status === 'Sold elsewhere',
    )
  }
  if (canMarkSold) return ['Available', 'Sold']
  if (canMarkSoldElsewhere) return ['Available', 'Sold elsewhere']
  return []
}

const INSPECTION_STATUS_OPTIONS = ['N/A', 'Need', 'Pending', 'Complete']
const EQUIPMENT_OPTIONS = ['yes', 'no', 'unknown']
const COLOR_OPTIONS = ['White', 'Blue', 'Yellow', 'Being painted']
const TIER_OPTIONS = ['', ...VEHICLE_TIERS]
const REAR_WHEEL_OPTIONS = ['SRW', 'DRW']
const YES_NO_OPTIONS = ['Yes', 'No']
const YES_NO_NA_OPTIONS = ['Yes', 'No', 'N/A']
const READY_FOR_TRANSPORT_OPTIONS = ['Ready', 'Not Ready']
const PICKUP_ADDRESS_OPTIONS = ['Complete', 'Incomplete', 'N/A']

/** Union of every model across every make — used when the header filter has no row context. */
const ALL_MODEL_OPTIONS = Array.from(new Set(makeOptions.flatMap(make => getVehicleModelOptions(make))))
/** Union of every size across every vehicle type — same reason as ALL_MODEL_OPTIONS. */
const ALL_SIZE_OPTIONS = Array.from(new Set(vehicleTypeOptions.flatMap(type => getVehicleSizeOptions(type))))
const YEAR_OPTIONS = getVehicleYearOptions().map(Number)

const formatMoney = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : formatCurrency(Number(value))

const formatNumber = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : new Intl.NumberFormat('en-US').format(Number(value))

const formatDate = (value: string | null | undefined): string => {
  if (!value) return ''
  const date = new Date(value)
  return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US')
}

const formatPercent = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? ''
    : `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)}%`

/** Display yes / no / unknown with a capital first letter. */
const formatEquipmentLabel = (value: unknown): string => {
  if (value === true || value === 'yes') return 'Yes'
  if (value === false || value === 'no') return 'No'
  if (value === null || value === undefined || value === 'unknown') return 'Unknown'
  const str = String(value)
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

const StatusCell = ({ value }: { value: string | null }) => {
  if (!value) return null
  const styles = getVehicleStatusStyles(value)
  return (
    <Chip
      size='small'
      label={value}
      sx={{ backgroundColor: styles.backgroundColor, color: styles.hex, fontWeight: 500 }}
    />
  )
}

const numberEditCellProps =
  (allowNegative: boolean) => (params: GridPreProcessEditCellProps) => {
    // Keep "12." / "." as non-errors so the decimal keystroke is not blocked mid-edit
    if (isIncompleteInventoryV3NumberInput(params.props.value)) {
      return { ...params.props, error: false }
    }
    const parsed = parseInventoryV3Number(params.props.value)
    const negative = !allowNegative && parsed.ok && parsed.value !== null && parsed.value < 0
    return { ...params.props, error: !parsed.ok || negative }
  }

const dateEditCellProps = (params: GridPreProcessEditCellProps) => {
  const parsed = parseInventoryV3Date(params.props.value)
  return { ...params.props, error: !parsed.ok }
}

const emailEditCellProps = (params: GridPreProcessEditCellProps) => {
  const value = params.props.value
  const blank = value == null || String(value).trim() === ''
  return { ...params.props, error: !blank && !verifyProperEmail(String(value).trim()) }
}

const phoneEditCellProps = (params: GridPreProcessEditCellProps) => {
  const value = params.props.value
  const blank = value == null || String(value).trim() === ''
  const digits = String(value ?? '').replace(/\D/g, '')
  return { ...params.props, error: !blank && digits.length !== 10 }
}

const vinEditCellProps = (params: GridPreProcessEditCellProps) => {
  const value = params.props.value
  const blank = value == null || String(value).trim() === ''
  return { ...params.props, error: !blank && String(value).trim().length !== 17 }
}

const NUMBER_FIELD_SET = new Set<string>(INVENTORY_V3_NUMBER_FIELDS)
const DATE_FIELD_SET = new Set<string>(INVENTORY_V3_DATE_FIELDS)

/** Commits as soon as a value is picked so the user does not have to click away. */
const CommitOnSelectEditCell = (props: GridRenderEditCellParams<InventoryV3Row>) => {
  const apiRef = useGridApiContext()

  return (
    <GridEditSingleSelectCell
      {...props}
      variant='standard'
      size='small'
      disableUnderline
      sx={{
        fontSize: 'inherit',
        height: '100%',
        '& .MuiSelect-select': {
          fontSize: 'inherit',
          lineHeight: 1.25,
          display: 'flex',
          alignItems: 'center',
          py: 0,
          pl: 1,
          pr: '22px !important',
          minHeight: '100%',
          boxSizing: 'border-box',
        },
        '& .MuiSelect-icon': {
          fontSize: '1.15em',
          right: 2,
        },
      }}
      onValueChange={async (_event, newValue) => {
        const api = apiRef.current
        if (!api) return
        await api.setEditCellValue({ id: props.id, field: props.field, value: newValue })
        if (api.getCellMode(props.id, props.field) === 'edit') {
          api.stopCellEditMode({ id: props.id, field: props.field })
        }
        // The closed select leaves the option text selected; drop that so the chip is not highlighted.
        requestAnimationFrame(() => window.getSelection()?.removeAllRanges())
      }}
    />
  )
}

const inspectionStatusChipSx = (value: string) => {
  if (value === 'Need') return { backgroundColor: 'error.main', color: 'white' }
  if (value === 'Pending') return { backgroundColor: 'warning.main', color: 'white' }
  if (value === 'Complete') return { backgroundColor: 'success.main', color: 'white' }
  return { backgroundColor: 'grey.400', color: 'white' }
}

const InspectionStatusCell = ({ value }: { value: string | null | undefined }) => {
  const label = value || 'N/A'
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
      <Chip
        label={label}
        size='small'
        sx={{
          ...inspectionStatusChipSx(label),
          fontWeight: 500,
          minWidth: 80,
          userSelect: 'none',
          '& .MuiChip-label': { userSelect: 'none' },
        }}
      />
    </Box>
  )
}

const DeliveredCell = ({ value }: { value: InventoryV3Row['delivered_to_customer'] }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 600,
        color: value === 'N/A' ? 'text.secondary' : value === 'Yes' ? 'success.main' : 'error.main',
      }}
    >
      {value}
    </Typography>
  </Box>
)

const StatusIconCell = ({
  ok,
  okTitle,
  badTitle,
}: {
  ok: boolean
  okTitle: string
  badTitle: string
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
    <Tooltip title={ok ? okTitle : badTitle}>
      {ok ? (
        <CheckCircleIcon sx={{ width: 24, height: 24, color: 'success.main' }} />
      ) : (
        <CancelIcon sx={{ width: 24, height: 24, color: 'error.main' }} />
      )}
    </Tooltip>
  </Box>
)

const LtvCell = ({ value }: { value: number | null }) => {
  if (value === null || value === undefined) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
        <Typography variant='body2' color='text.secondary'>
          N/A
        </Typography>
      </Box>
    )
  }
  const color = value > 105 ? 'success.main' : value < 95 ? 'error.main' : 'primary.main'
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
      <Typography variant='body2' sx={{ color, fontWeight: 500 }}>
        {formatPercent(value)}
      </Typography>
    </Box>
  )
}

const profitTextColor = (value: number | null | undefined): string => {
  if (value === null || value === undefined || value === 0) return 'text.primary'
  if (value > 0) return 'success.main'
  return 'error.main'
}

const ProfitCell = ({ value }: { value: number | null }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
    <Typography variant='body2' sx={{ color: profitTextColor(value) }}>
      {formatMoney(value)}
    </Typography>
  </Box>
)

const DealQualityCell = ({ value }: { value: string | null }) => {
  if (!value) return null
  const info = getDealQualityInfo(value as Vehicle['dealQuality'])
  return <Chip size='small' color={info.color} variant='outlined' label={info.displayText} />
}

const SafetyCell = ({ row }: { row: InventoryV3Row }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, height: '100%' }}>
    <Tooltip title='Cameras'>
      <VideocamIcon sx={{ fontSize: 18, color: row.safety.cameras }} />
    </Tooltip>
    <Tooltip title='Sensors'>
      <SensorsIcon sx={{ fontSize: 18, color: row.safety.sensors }} />
    </Tooltip>
    <Tooltip title='Anti-theft'>
      <LockIcon sx={{ fontSize: 18, color: row.safety.antiTheft }} />
    </Tooltip>
  </Box>
)

const BooleanSwitchCell = ({
  checked,
  disabled,
  onToggle,
}: {
  checked: boolean
  disabled: boolean
  onToggle: () => void
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
    <Switch
      size='small'
      color='primary'
      checked={checked}
      disabled={disabled}
      onClick={event => event.stopPropagation()}
      onChange={onToggle}
    />
  </Box>
)

const FavoriteCell = ({
  isFavorite,
  disabled,
  onToggle,
}: {
  isFavorite: boolean
  disabled: boolean
  onToggle: () => void
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
    <IconButton
      size='small'
      disabled={disabled}
      onClick={event => {
        event.stopPropagation()
        if (!disabled) onToggle()
      }}
      sx={{
        color: isFavorite ? 'warning.main' : 'text.secondary',
        '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.08)' },
      }}
    >
      {isFavorite ? '★' : '☆'}
    </IconButton>
  </Box>
)

const VEHICLE_ICONS = {
  'step van': stepVanIcon,
  'box truck': boxTruckIcon,
  'cargo van': cargoVanIcon,
  cutaway: cutawayIcon,
  default: stepVanIcon,
}

const VehicleTypeIcon = ({ type, onClick }: { type: string | null; onClick: () => void }) => {
  const iconType = type ? type.toLowerCase() : 'default'
  const iconSrc = VEHICLE_ICONS[iconType as keyof typeof VEHICLE_ICONS] || VEHICLE_ICONS.default

  return (
    <Box
      onClick={event => {
        event.stopPropagation()
        onClick()
      }}
      role='img'
      aria-label={`${type || 'Default'} vehicle`}
      sx={{
        width: 48,
        height: 48,
        backgroundImage: `url(${iconSrc})`,
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        borderRadius: 1,
        cursor: 'pointer',
      }}
    />
  )
}

const isInHouseTitlePhotoUrl = (url: string) => {
  let fileName = url
  try {
    fileName = decodeURIComponent(url).split('/').pop()?.split('?')[0] || url
  } catch {
    fileName = url.split('/').pop()?.split('?')[0] || url
  }
  const lower = fileName.toLowerCase()
  return lower.includes('title_front_') || lower.includes('title_back_')
}

const getDocumentPresence = (vehicle: Vehicle) => {
  const titleCopies = vehicle.documents?.title_copies || []
  return {
    hasTitleCopy: titleCopies.some(url => !isInHouseTitlePhotoUrl(url)),
    hasInHouseTitlePhotos: titleCopies.some(isInHouseTitlePhotoUrl),
    hasPurchaseOrder: (vehicle.documents?.purchase_orders?.length ?? 0) > 0,
    hasPaymentCopy: (vehicle.documents?.payment_copies?.length ?? 0) > 0,
  }
}

/**
 * singleSelect with 3+ options: multi-select via `isAnyOf` (sidebar + custom header filter).
 * Keep `is` too: DataGridPro header filters strip `isAnyOf` and crash if no operator remains.
 * Two-option columns stay single-select (`is` only).
 */
const SINGLE_SELECT_IS_FILTER_OPERATOR = getGridSingleSelectOperators().filter(operator => operator.value === 'is')
const SINGLE_SELECT_MULTI_FILTER_OPERATORS = getGridSingleSelectOperators().filter(
  operator => operator.value === 'isAnyOf' || operator.value === 'is',
)

const resolvedValueOptionCount = (column: GridColDef<InventoryV3Row>): number => {
  if (!('valueOptions' in column) || column.valueOptions == null) return 0
  const options = column.valueOptions
  const resolved =
    typeof options === 'function' ? options({ field: column.field } as { field: string }) : options
  return Array.isArray(resolved) ? resolved.length : 0
}

export const getInventoryV3Columns = ({
  isAdmin,
  isBabyAdmin,
  canModify,
  canViewPurchasePrice,
  canViewProfit,
  canViewProfitRealized,
  canMarkSold,
  canMarkSoldElsewhere,
  canMarkReadyForSale,
  onEditVehicleClick,
  onManagePhotosClick,
  onManageDocumentsClick,
  onOpenTimelineClick,
  onBooleanFieldChange,
}: InventoryV3ColumnParams): GridColDef<InventoryV3Row>[] => {
  const editable = (extra = true) => canModify && extra
  const statusOptions = getStatusValueOptions({ canModify, canMarkSold, canMarkSoldElsewhere })

  const columns: (GridColDef<InventoryV3Row> | null)[] = [
    {
      field: 'edit',
      headerName: 'Icon',
      width: 80,
      sortable: false,
      filterable: false,
      editable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
        <Tooltip title='Edit vehicle'>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <VehicleTypeIcon type={params.row.type} onClick={() => onEditVehicleClick(params.row.vehicle)} />
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'photos',
      headerName: 'Photo',
      width: 80,
      sortable: false,
      editable: false,
      type: 'singleSelect',
      valueOptions: YES_NO_OPTIONS,
      valueGetter: (_value, row) => (row.vehicle.photo_storage_path ? 'Yes' : 'No'),
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => {
        const hasPhotos = Boolean(params.row.vehicle.photo_storage_path)
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              cursor: 'pointer',
            }}
            onClick={event => {
              event.stopPropagation()
              onManagePhotosClick(params.row.vehicle)
            }}
          >
            <Tooltip title={hasPhotos ? 'Has photos! Click to manage photos' : 'Photos needed! Click to manage photos'}>
              {hasPhotos ? (
                <PhotoCameraIcon sx={{ width: 24, height: 24, color: 'success.main' }} />
              ) : (
                <NoPhotographyIcon sx={{ width: 20, height: 20, color: 'primary.main' }} />
              )}
            </Tooltip>
          </Box>
        )
      },
    },
    {
      field: 'documents',
      headerName: 'Docs',
      width: 150,
      sortable: false,
      filterable: false,
      editable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => {
        const { hasTitleCopy, hasInHouseTitlePhotos, hasPurchaseOrder, hasPaymentCopy } = getDocumentPresence(
          params.row.vehicle,
        )
        const openDocs = (tab: number) => (event: MouseEvent) => {
          event.stopPropagation()
          onManageDocumentsClick(params.row.vehicle, tab)
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              gap: 0.5,
            }}
          >
            <Tooltip title={hasTitleCopy ? 'Title copy uploaded' : 'Title copy needed'}>
              <ArticleIcon
                onClick={openDocs(0)}
                sx={{ width: 18, height: 18, cursor: 'pointer', color: hasTitleCopy ? 'success.main' : 'primary.main' }}
              />
            </Tooltip>
            <Tooltip title={hasInHouseTitlePhotos ? 'In house title photos uploaded' : 'In house title photos needed'}>
              <CameraAltIcon
                onClick={openDocs(0)}
                sx={{
                  width: 18,
                  height: 18,
                  cursor: 'pointer',
                  color: hasInHouseTitlePhotos ? 'success.main' : 'primary.main',
                }}
              />
            </Tooltip>
            <Tooltip title={hasPurchaseOrder ? 'Purchase order uploaded' : 'Purchase order needed'}>
              <ReceiptLongIcon
                onClick={openDocs(1)}
                sx={{
                  width: 18,
                  height: 18,
                  cursor: 'pointer',
                  color: hasPurchaseOrder ? 'success.main' : 'primary.main',
                }}
              />
            </Tooltip>
            <Tooltip title={hasPaymentCopy ? 'Payment copy uploaded' : 'Payment copy needed'}>
              <PaymentIcon
                onClick={openDocs(2)}
                sx={{
                  width: 18,
                  height: 18,
                  cursor: 'pointer',
                  color: hasPaymentCopy ? 'success.main' : 'primary.main',
                }}
              />
            </Tooltip>
          </Box>
        )
      },
    },
    {
      field: 'title_state',
      headerName: 'Title State',
      width: 120,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: states.map(state => state.abbreviation),
      valueFormatter: (value: string | null) => {
        if (!value) return ''
        const state = states.find(entry => entry.abbreviation === value)
        return state ? `${state.abbreviation}` : value
      },
    },
    {
      field: 'timeline',
      headerName: 'Timeline',
      width: 80,
      sortable: false,
      filterable: false,
      editable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            cursor: 'pointer',
          }}
          onClick={event => {
            event.stopPropagation()
            onOpenTimelineClick(params.row.vehicle)
          }}
        >
          <Tooltip
            title={
              params.row.isInPackage
                ? params.row.isDelivered
                  ? 'Delivered - view timeline'
                  : 'View timeline'
                : 'Vehicle not in a package'
            }
          >
            <TimelineIcon
              sx={{
                width: 24,
                height: 24,
                color: params.row.isDelivered
                  ? 'success.main'
                  : params.row.isInPackage
                    ? 'primary.main'
                    : 'text.disabled',
              }}
            />
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'delivered_to_customer',
      headerName: 'Delivered',
      width: 110,
      editable: false,
      type: 'singleSelect',
      valueOptions: YES_NO_NA_OPTIONS,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
        <DeliveredCell value={params.row.delivered_to_customer} />
      ),
    },
    {
      field: 'mechanical_status',
      headerName: 'Mechanical',
      width: 160,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: INSPECTION_STATUS_OPTIONS,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
        <InspectionStatusCell value={params.row.mechanical_status} />
      ),
      renderEditCell: params => <CommitOnSelectEditCell {...params} />,
    },
    {
      field: 'body_status',
      headerName: 'Body',
      width: 160,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: INSPECTION_STATUS_OPTIONS,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
        <InspectionStatusCell value={params.row.body_status} />
      ),
      renderEditCell: params => <CommitOnSelectEditCell {...params} />,
    },
    {
      field: 'safety',
      headerName: 'Safety',
      width: 90,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => <SafetyCell row={params.row} />,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: vehicleTypeOptions,
    },
    {
      field: 'stock_number',
      headerName: 'Stock #',
      width: 120,
      editable: false,
    },
    { field: 'who', headerName: 'Who', width: 110, editable: editable() },
    {
      field: 'condition',
      headerName: 'Condition',
      width: 110,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: conditionOptions,
    },
    {
      field: 'tier',
      headerName: 'Tier',
      width: 120,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: TIER_OPTIONS,
      valueFormatter: (value: string | null) => value || '',
    },
    {
      field: 'color',
      headerName: 'Color',
      width: 120,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: COLOR_OPTIONS,
    },
    {
      field: 'year',
      headerName: 'Year',
      width: 90,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: YEAR_OPTIONS,
    },
    {
      field: 'make',
      headerName: 'Make',
      width: 130,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: makeOptions,
    },
    {
      field: 'model',
      headerName: 'Model',
      width: 130,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: ({ row }: GridValueOptionsParams<InventoryV3Row>) =>
        row?.make ? getVehicleModelOptions(row.make) : ALL_MODEL_OPTIONS,
    },
    {
      field: 'size',
      headerName: 'Size',
      width: 130,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: ({ row }: GridValueOptionsParams<InventoryV3Row>) =>
        row?.type ? getVehicleSizeOptions(row.type) : ALL_SIZE_OPTIONS,
    },
    {
      field: 'miles',
      headerName: 'Miles',
      width: 110,
      type: 'number',
      editable: editable(),
      valueFormatter: (value: number | null) => formatNumber(value),
    },
    {
      field: 'dealQuality',
      headerName: 'Deal',
      width: 120,
      editable: false,
      type: 'singleSelect',
      valueOptions: dealQualityOptions,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => <DealQualityCell value={params.row.dealQuality} />,
    },
    canViewPurchasePrice
      ? {
          field: 'seller_asking_price',
          headerName: 'Purchase',
          width: 130,
          type: 'number',
          editable: editable(),
          valueFormatter: (value: number | null) => formatMoney(value),
        }
      : null,
    {
      field: 'optic_list_price',
      headerName: 'List Price',
      width: 130,
      type: 'number',
      editable: editable(),
      valueFormatter: (value: number | null) => formatMoney(value),
    },
    {
      field: 'grayBookPrice',
      headerName: 'Gray Book',
      width: 130,
      type: 'number',
      editable: false,
      valueFormatter: (value: number | null) => formatMoney(value),
    },
    {
      field: 'ltv',
      headerName: 'LTV %',
      width: 100,
      type: 'number',
      editable: false,
      valueFormatter: (value: number | null) => formatPercent(value) || 'N/A',
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => <LtvCell value={params.row.ltv} />,
    },
    canViewProfit
      ? {
          field: 'profit',
          headerName: 'Profit',
          width: 120,
          type: 'number',
          editable: false,
          valueFormatter: (value: number | null) => formatMoney(value),
          renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
            <ProfitCell value={params.row.profit} />
          ),
        }
      : null,
    canViewProfitRealized
      ? {
          field: 'profit_realized',
          headerName: 'Profit Realized',
          width: 160,
          type: 'boolean',
          editable: false,
          renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
            <BooleanSwitchCell
              checked={params.row.profit_realized === true}
              disabled={!canModify}
              onToggle={() =>
                onBooleanFieldChange(params.row.id, 'profit_realized', !params.row.profit_realized)
              }
            />
          ),
        }
      : null,
    isAdmin ? { field: 'vin', headerName: 'VIN', width: 190, editable: editable() } : null,
    {
      field: 'link',
      headerName: 'Link',
      width: 100,
      editable: false,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) =>
        params.row.link ? (
          <MuiLink
            href={params.row.link}
            target='_blank'
            rel='noopener noreferrer'
            onClick={event => event.stopPropagation()}
          >
            <Typography variant='body2'>Open</Typography>
          </MuiLink>
        ) : null,
    },
    {
      field: 'pickup_address',
      headerName: 'Pickup Address',
      width: 150,
      editable: false,
      type: 'singleSelect',
      valueOptions: PICKUP_ADDRESS_OPTIONS,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) =>
        params.row.pickup_address === 'N/A' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <Tooltip title='Refinance trucks do not get a pickup address'>
              <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 600 }}>
                N/A
              </Typography>
            </Tooltip>
          </Box>
        ) : (
          <StatusIconCell
            ok={params.row.pickup_address === 'Complete'}
            okTitle='Pickup address complete'
            badTitle='Pickup address incomplete'
          />
        ),
    },
    {
      field: 'ready_for_transport',
      headerName: 'Ready for Transport',
      width: 150,
      editable: false,
      type: 'singleSelect',
      valueOptions: READY_FOR_TRANSPORT_OPTIONS,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
        <StatusIconCell
          ok={params.row.ready_for_transport === 'Ready'}
          okTitle='Ready for transport'
          badTitle='Not ready for transport'
        />
      ),
    },
    {
      field: 'refinance',
      headerName: 'Refi',
      width: 90,
      type: 'boolean',
      editable: false,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
        <StatusIconCell
          ok={params.row.refinance === true}
          okTitle='Refinance'
          badTitle='Not refinance'
        />
      ),
    },
    { field: 'id', headerName: 'ID', width: 200, editable: false },
    {
      field: 'date_added',
      headerName: 'Date Added',
      width: 160,
      editable: false,
      type: 'date',
      filterOperators: INVENTORY_V3_DATE_FILTER_OPERATORS,
      valueGetter: (value: string | null) => inventoryV3DateCellValue(value),
      valueFormatter: (value: Date | string | null) =>
        formatDate(value instanceof Date ? value.toISOString() : value),
      sortComparator: (left: Date | string | null, right: Date | string | null) => {
        const leftTime = left ? new Date(left).getTime() : 0
        const rightTime = right ? new Date(right).getTime() : 0
        return leftTime - rightTime
      },
    },
    {
      field: 'fuel',
      headerName: 'Fuel',
      width: 100,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: vehicleFuelOptions,
    },
    { field: 'location', headerName: 'Location', width: 150, editable: editable() },
    {
      field: 'sold_date',
      headerName: 'Sold Date',
      width: 160,
      editable: editable(),
      type: 'date',
      filterOperators: INVENTORY_V3_DATE_FILTER_OPERATORS,
      valueGetter: (value: string | null) => inventoryV3DateCellValue(value),
      valueSetter: (value, row) => ({ ...row, sold_date: inventoryV3DateStoredValue(value) }),
      valueFormatter: (value: Date | string | null) =>
        formatDate(value instanceof Date ? value.toISOString() : value),
    },
    {
      field: 'date_available',
      headerName: 'Date Available',
      width: 160,
      editable: editable(),
      type: 'date',
      filterOperators: INVENTORY_V3_DATE_FILTER_OPERATORS,
      valueGetter: (value: string | null) => inventoryV3DateCellValue(value),
      valueSetter: (value, row) => ({ ...row, date_available: inventoryV3DateStoredValue(value) }),
      valueFormatter: (value: Date | string | null) =>
        formatDate(value instanceof Date ? value.toISOString() : value),
    },
    { field: 'sold_to_name', headerName: 'Sold To', width: 160, editable: false },
    { field: 'sold_by_name', headerName: 'Sold By', width: 160, editable: false },
    { field: 'selling_to_name', headerName: 'Selling To', width: 160, editable: false },
    { field: 'bought_from', headerName: 'Bought From', width: 160, editable: editable() },
    {
      field: 'rear_wheel',
      headerName: 'Rear Wheel',
      width: 120,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: REAR_WHEEL_OPTIONS,
    },
    { field: 'truck_notes', headerName: 'Truck Notes', width: 220, editable: false },
    { field: 'phone_number', headerName: 'Phone', width: 140, editable: editable() },
    { field: 'email', headerName: 'Email', width: 200, editable: editable() },
    { field: 'follow_up_notes', headerName: 'Follow Up Notes', width: 220, editable: editable() },
    { field: 'entered_by', headerName: 'Entered By', width: 160, editable: editable() },
    {
      field: 'lien_payoff_amount',
      headerName: 'Lien Payoff',
      width: 130,
      type: 'number',
      editable: editable(),
      valueFormatter: (value: number | null) => formatMoney(value),
    },
    {
      field: 'repair_cost',
      headerName: 'Repair Cost',
      width: 130,
      type: 'number',
      editable: editable(),
      valueFormatter: (value: number | null) => formatMoney(value),
    },
    {
      field: 'estimated_repair_cost',
      headerName: 'Est. Repair Cost',
      width: 140,
      type: 'number',
      editable: editable(),
      valueFormatter: (value: number | null) => formatMoney(value),
    },
    {
      field: 'shipping_cost',
      headerName: 'Shipping Cost',
      width: 130,
      type: 'number',
      editable: editable(),
      valueFormatter: (value: number | null) => formatMoney(value),
    },
    { field: 'show_on_website', headerName: 'On Website', width: 120, type: 'boolean', editable: false,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
        <BooleanSwitchCell
          checked={params.row.show_on_website === true}
          disabled={!canModify || (params.row.status?.toLowerCase() === 'sold' && !params.row.show_on_website)}
          onToggle={() =>
            onBooleanFieldChange(params.row.id, 'show_on_website', !params.row.show_on_website)
          }
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      editable: (canModify || canMarkSold || canMarkSoldElsewhere) && statusOptions.length > 0,
      type: 'singleSelect',
      valueOptions: statusOptions,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => <StatusCell value={params.row.status} />,
      renderEditCell: params => <CommitOnSelectEditCell {...params} />,
    },
    {
      field: 'cameras',
      headerName: 'Cameras',
      width: 110,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: EQUIPMENT_OPTIONS,
      valueFormatter: (value: string | null) => formatEquipmentLabel(value),
    },
    {
      field: 'shelves',
      headerName: 'Shelves',
      width: 110,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: EQUIPMENT_OPTIONS,
      valueFormatter: (value: string | null) => formatEquipmentLabel(value),
    },
    {
      field: 'gwvr_over_10k',
      headerName: 'GVWR > 10k',
      width: 120,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: EQUIPMENT_OPTIONS,
    },
    { field: 'favorite', headerName: 'Favorite', width: 100, type: 'boolean', editable: false,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
        <FavoriteCell
          isFavorite={params.row.favorite === true}
          disabled={!canModify}
          onToggle={() => onBooleanFieldChange(params.row.id, 'favorite', !params.row.favorite)}
        />
      ),
    },
    { field: 'paid_for', headerName: 'Paid For', width: 100, type: 'boolean', editable: false,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
        <BooleanSwitchCell
          checked={params.row.paid_for === true}
          disabled={!canModify}
          onToggle={() => onBooleanFieldChange(params.row.id, 'paid_for', !params.row.paid_for)}
        />
      ),
    },
    { field: 'ready_for_sale', headerName: 'Ready for Sale', width: 130, type: 'boolean', editable: false,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
        <BooleanSwitchCell
          checked={params.row.ready_for_sale === true}
          disabled={!canMarkReadyForSale}
          onToggle={() =>
            onBooleanFieldChange(params.row.id, 'ready_for_sale', !params.row.ready_for_sale)
          }
        />
      ),
    },
    {
      field: 'funded',
      headerName: 'Funded',
      width: 100,
      type: 'boolean',
      editable: false,
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
        <BooleanSwitchCell
          checked={params.row.funded === true}
          disabled={!canModify}
          onToggle={() => onBooleanFieldChange(params.row.id, 'funded', !params.row.funded)}
        />
      ),
    },
    {
      field: 'size_confirmed',
      headerName: 'Size Confirmed',
      width: 130,
      editable: editable(),
      type: 'singleSelect',
      valueOptions: EQUIPMENT_OPTIONS,
      valueGetter: (_value, row) => {
        if (row.size_confirmed === true) return 'yes'
        if (row.size_confirmed === false) return 'no'
        return 'unknown'
      },
      valueSetter: (value, row) => ({
        ...row,
        size_confirmed: value === 'yes',
      }),
      renderCell: (params: GridRenderCellParams<InventoryV3Row>) => (
        <StatusIconCell
          ok={params.row.size_confirmed === true}
          okTitle='Size confirmed'
          badTitle='Size not confirmed'
        />
      ),
    },
  ]

  const restrictedFields: string[] = []
  if (!canViewPurchasePrice) restrictedFields.push('seller_asking_price')
  if (!canViewProfit) restrictedFields.push('profit')
  if (!canViewProfitRealized) restrictedFields.push('profit_realized')
  if (!canMarkReadyForSale) restrictedFields.push('ready_for_sale')
  if (!isAdmin && !isBabyAdmin) restrictedFields.push('who')
  if (!isAdmin) restrictedFields.push('vin', 'phone_number', 'show_on_website', 'dealQuality')

  const visibleColumns = columns.filter((column): column is GridColDef<InventoryV3Row> => {
    if (!column) return false
    return !restrictedFields.includes(column.field)
  })

  const rank = (field: string) => {
    const index = (DEFAULT_V3_COLUMN_ORDER as readonly string[]).indexOf(field)
    return index === -1 ? DEFAULT_V3_COLUMN_ORDER.length + 1 : index
  }

  return visibleColumns
    .map(column => {
      const withWidth = column
      if (withWidth.type === 'singleSelect' && withWidth.editable && !withWidth.renderEditCell) {
        return {
          ...withWidth,
          renderEditCell: (params: GridRenderEditCellParams<InventoryV3Row>) => (
            <CommitOnSelectEditCell {...params} />
          ),
        }
      }
      if (NUMBER_FIELD_SET.has(withWidth.field) && withWidth.editable) {
        return {
          ...withWidth,
          valueParser: (value: unknown) => {
            // Do not coerce "12." → 12 while typing — that drops the decimal point
            if (isIncompleteInventoryV3NumberInput(value)) return value
            const parsed = parseInventoryV3Number(value)
            return parsed.ok ? parsed.value : value
          },
          preProcessEditCellProps: numberEditCellProps(false),
          renderEditCell: withWidth.renderEditCell
            ? withWidth.renderEditCell
            : (params: GridRenderEditCellParams<InventoryV3Row>) => (
                <GridEditInputCell {...params} type='text' inputProps={{ inputMode: 'decimal', min: 0 }} />
              ),
        }
      }
      if (DATE_FIELD_SET.has(withWidth.field) && withWidth.editable) {
        return { ...withWidth, preProcessEditCellProps: dateEditCellProps }
      }
      if (withWidth.field === 'email' && withWidth.editable) {
        return { ...withWidth, preProcessEditCellProps: emailEditCellProps }
      }
      if (withWidth.field === 'phone_number' && withWidth.editable) {
        return { ...withWidth, preProcessEditCellProps: phoneEditCellProps }
      }
      if (withWidth.field === 'vin' && withWidth.editable) {
        return { ...withWidth, preProcessEditCellProps: vinEditCellProps }
      }
      return withWidth
    })
    .toSorted((left, right) => rank(left.field) - rank(right.field))
    .map(column => {
      if (column.type !== 'boolean') return column
      const field = column.field as keyof InventoryV3Row
      return {
        ...column,
        type: 'singleSelect' as const,
        valueOptions: YES_NO_OPTIONS,
        valueGetter:
          column.valueGetter ??
          ((_value: unknown, row: InventoryV3Row) => (row[field] === true ? 'Yes' : 'No')),
      }
    })
    .map(column => {
      if (column.type !== 'singleSelect') return column
      const existingHeaderClass =
        typeof column.headerClassName === 'string' ? column.headerClassName : undefined
      const useMultiSelect = resolvedValueOptionCount(column) > 2
      return {
        ...column,
        filterOperators: useMultiSelect
          ? SINGLE_SELECT_MULTI_FILTER_OPERATORS
          : SINGLE_SELECT_IS_FILTER_OPERATOR,
        // Header filters do not support isAnyOf natively — custom multi-select when 3+ options.
        renderHeaderFilter: useMultiSelect
          ? (column.renderHeaderFilter ?? (params => <SingleSelectHeaderMultiFilter {...params} />))
          : column.renderHeaderFilter,
        headerClassName: [existingHeaderClass, INVENTORY_V3_SINGLE_SELECT_HEADER_FILTER_CLASS].filter(Boolean).join(' '),
      }
    })
}
